import Map, { NavigationControl, MapRef, FullscreenControl, Marker } from 'react-map-gl/mapbox';
import ShopMarker from '../RouteMap/ShopMarker';
import EndingLocationMarker from '../RouteMap/EndingLocationMarker';
import MapShopAddress from "./MapShopAddress";
import RouteViewMapLegend from "./RouteViewMapLegend";
import { DEFAULT_MAP_ZOOM } from '../RouteAdd/routeAddModel';
import { RouteViewState } from "./routeViewModel";
import { findRouteJobDetails } from "../routingModel";
import { AddJobsRecord } from "../routingApi";
import MapStyleDropdown from "../../ScheduleManagement/DayView/MapStyleDropdown";

interface RouteViewMapProps {
  state: RouteViewState;
  mapRef: React.RefObject<MapRef>;
  hoveredJobSortIndex: number | null;
  setHoveredJobSortIndex: (sortIndex: number | null) => void;
  setIsMapLoaded: (loaded: boolean) => void;
  mapStyle: string;
  setMapStyle: (style: string) => void;
  isStyleChanging: boolean;
  setIsStyleChanging: (changing: boolean) => void;
  currentRouteData: any;
  displayRouteOnMap: (routeData: any, animate: boolean) => void;
  displayDrivingDirections: () => void;
  shopLocation: any;
  tenantLocations?: any[];
  onManageShopAddress: () => void;
  jobsForCreateRoute: AddJobsRecord[] | undefined;
}

const RouteViewMap = ({ 
  state,
  mapRef, 
  hoveredJobSortIndex, 
  setHoveredJobSortIndex, 
  setIsMapLoaded, 
  mapStyle, 
  setMapStyle, 
  isStyleChanging, 
  setIsStyleChanging, 
  currentRouteData, 
  displayRouteOnMap, 
  displayDrivingDirections, 

  shopLocation, 
  tenantLocations, 
  onManageShopAddress, 
  jobsForCreateRoute 
}: RouteViewMapProps) => {
  return (
    <div style={{ flex: 1, position: 'relative', background: '#eee'}}>
      <Map
        ref={mapRef}
        mapboxAccessToken={(window as any).env.MAPBOX_ACCESS_TOKEN}
        initialViewState={shopLocation ? {
          longitude: shopLocation.longitude,
          latitude: shopLocation.latitude,
          zoom: DEFAULT_MAP_ZOOM,
          bearing: 0,
          pitch: 0,
          padding: { top: 0, bottom: 0, left: 0, right: 0 }
        } : undefined}
        onLoad={() => {
          setIsMapLoaded(true);
          displayDrivingDirections()
        }}
        onStyleData={() => {
          if (isStyleChanging && currentRouteData) {
            setTimeout(() => {
              displayRouteOnMap(currentRouteData, false);
            }, 100);
            setIsStyleChanging(false);
          }
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
      >
        <NavigationControl position="top-left" />
        <FullscreenControl position="bottom-left" />
        
        {/* Satellite View Toggle */}
        <MapStyleDropdown
          mapStyle={mapStyle}
          setMapStyle={setMapStyle}
          setIsStyleChanging={setIsStyleChanging}
        />
        
        {/* Starting Location Marker */}
        {state.startingLocation && state.startingLocation.latitude && state.startingLocation.longitude && (
          <ShopMarker latitude={state.startingLocation.latitude} longitude={state.startingLocation.longitude} />
        )}
        {/* Ending Location Marker */}
        {state.endingLocation && state.endingLocation.latitude && state.endingLocation.longitude && 
         (!state.startingLocation || 
          state.startingLocation.latitude !== state.endingLocation.latitude || 
          state.startingLocation.longitude !== state.endingLocation.longitude) && (
          <EndingLocationMarker latitude={state.endingLocation.latitude} longitude={state.endingLocation.longitude} />
        )}
        {/* Route Job Markers */}
        {state.jobs && state.jobs.length > 0 && state.jobs
          .slice()
          .sort((a, b) => a.sortIndex - b.sortIndex)
          .map((routeJob) => {
            const job = findRouteJobDetails(routeJob, jobsForCreateRoute);
            if (!job || !job.latitude || !job.longitude) {
              return null;
            }
            if (state.startingLocation && String(routeJob.jobId) === state.startingLocation.id.replace('job-', '')) {
              return null;
            }
            if (state.endingLocation && String(routeJob.jobId) === state.endingLocation.id.replace('job-', '')) {
              return null;
            }
            const isHovered = hoveredJobSortIndex === routeJob.sortIndex;
            const isStartingLocation = state.startingLocation && String(routeJob.jobId) === state.startingLocation.id.replace('job-', '');
            const isEndingLocation = state.endingLocation && String(routeJob.jobId) === state.endingLocation.id.replace('job-', '');
            const isDisabled = isStartingLocation || isEndingLocation;

            const visibleIndex = state.jobs
              .slice()
              .sort((a, b) => a.sortIndex - b.sortIndex)
              .filter(j => {
                const job = findRouteJobDetails(j, jobsForCreateRoute);
                if (!job) {
                  return false;
                }
                const isStarting = state.startingLocation && String(j.jobId) === state.startingLocation.id.replace('job-', '');
                const isEnding = state.endingLocation && String(j.jobId) === state.endingLocation.id.replace('job-', '');
                return !isStarting && !isEnding;
              })
              .findIndex(j => j.sortIndex === routeJob.sortIndex);

            const markerStyle = {
              position: 'relative' as React.CSSProperties['position'],
              width: '30px',
              height: '45px',
              display: 'flex',
              flexDirection: 'column' as React.CSSProperties['flexDirection'],
              alignItems: 'center',
            };
            const pinHeadStyle = {
              width: '30px',
              height: '30px',
              backgroundColor: '#FFCABD',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: '2px solid #FF5C35',
              zIndex: 2,
              position: 'relative' as React.CSSProperties['position'],
              boxShadow: isHovered ? '0 0 16px 6px #FFCABD' : undefined,
              transition: 'box-shadow 0.2s, transform 0.2s',
              cursor: isHovered ? 'pointer' : 'default',
              transform: isHovered ? 'scale(1.15)' : 'scale(1)',
            };
            const pinTailStyle = {
              width: '20px',
              height: '20px',
              backgroundColor: '#FFCABD',
              clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
              marginTop: '-5px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              zIndex: 1,
            };
            return (
              <Marker
                key={routeJob.sortIndex}
                latitude={job.latitude}
                longitude={job.longitude}
                anchor="bottom"
              >
                <div
                  style={markerStyle}
                  onMouseEnter={() => setHoveredJobSortIndex(routeJob.sortIndex)}
                  onMouseLeave={() => setHoveredJobSortIndex(null)}
                >
                  <div style={pinHeadStyle}>
                    <span style={{ color: '#FF5C35', fontWeight: 'bold', fontSize: 14 }}>
                      {isDisabled ? '-' : (visibleIndex !== undefined ? visibleIndex + 1 : routeJob.sortIndex + 1)}
                    </span>
                  </div>
                  <div style={pinTailStyle} />
                </div>
              </Marker>
            );
          })}
        <MapShopAddress 
          tenantLocations={tenantLocations} 
          onManageShopAddress={onManageShopAddress}
        />
      </Map>
      <RouteViewMapLegend />
    </div>
  );
};

export default RouteViewMap;